import React, { useState, useEffect } from 'react';
import { css, cx } from 'emotion';
import ArrowRightIcon from '@frontend/static/icons/arrow-right.svg';
import { brandText, brandAlt, neutral, } from '@guardian/src-foundations/palette';
import { textSans, headline } from '@guardian/src-foundations/typography';
import { from, until } from '@guardian/src-foundations/mq';
import { LinkButton, buttonBrand } from '@guardian/src-button';
import { shouldHideSupportMessaging } from '@root/src/web/lib/contributions';
import { fetchTickerData, TickerCountType, } from '@root/src/lib/fetchTickerData';
import { addForMinutes, getCookie } from '@root/src/web/browser/cookie';
import { ThemeProvider } from "@root/node_modules/emotion-theming";
const paddingStyles = css `
    ${until.mobileLandscape} {
        padding-left: 10px;
    }
    ${until.tablet} {
        padding-top: 33px;
    }
    ${from.mobileLandscape} {
        padding-left: 20px;
    }
`;
const messageStyles = css `
    color: ${brandAlt[400]};
    ${headline.xxsmall({ fontWeight: 'bold' })};
    padding-top: 3px;
    margin-bottom: 3px;

    ${from.desktop} {
        ${headline.xsmall({ fontWeight: 'bold' })}
    }

    ${from.leftCol} {
        ${headline.medium({ fontWeight: 'bold' })}
    }
`;
const linkStyles = css `
    background: ${brandAlt[400]};
    border-radius: 16px;
    box-sizing: border-box;
    color: ${neutral[7]};
    float: left;
    ${textSans.small()};
    font-weight: 700;
    height: 32px;
    text-decoration: none;
    padding: 6px 12px 0 12px;
    line-height: 18px;
    position: relative;
    margin-right: 10px;
    margin-bottom: 6px;

    ${from.mobileMedium} {
        padding-right: 34px;
    }

    svg {
        fill: currentColor;
        position: absolute;
        right: 3px;
        top: 50%;
        height: 32px;
        width: 32px;
        transform: translate(0, -50%);
        transition: transform 0.3s ease-in-out;

        ${until.mobileMedium} {
            display: none;
        }
    }

    :hover svg {
        transform: translate(3px, -50%);
    }
`;
const hidden = css `
    display: none;
`;
const hiddenUntilTablet = css `
    ${until.tablet} {
        display: none;
    }
`;
const hiddenFromTablet = css `
    ${from.tablet} {
        display: none;
    }
`;
const subMessageStyles = css `
    color: ${brandText.primary};
    ${textSans.medium()};
    margin-bottom: 5px;
`;
const headerYellowHighlight = css `
    color: ${brandAlt[400]};
    font-weight: 700;
`;
const AUS_MOMENT_SUPPORTER_COUNT_COOKIE_NAME = 'gu_aus_moment_supporter_count';
export const ReaderRevenueLinks = ({ edition, urls, dataLinkNamePrefix, inHeader, enableAusMoment2020Header, }) => {
    const [numberOfSupporters, setnumberOfSupporters] = useState('');
    const showAusMomentHeader = edition === 'AU' && enableAusMoment2020Header;
    useEffect(() => {
        if (showAusMomentHeader) {
            const cookieValue = getCookie(AUS_MOMENT_SUPPORTER_COUNT_COOKIE_NAME);
            if (cookieValue) {
                setnumberOfSupporters(cookieValue.toLocaleString());
            }
            else {
                fetchTickerData(TickerCountType.people).then((td) => {
                    addForMinutes(AUS_MOMENT_SUPPORTER_COUNT_COOKIE_NAME, `${td.total}`, 60);
                    setnumberOfSupporters(td.total.toLocaleString());
                });
            }
        }
    }, [showAusMomentHeader]);
    if (shouldHideSupportMessaging()) {
        if (showAusMomentHeader) {
            return (React.createElement("div", { className: cx(inHeader && paddingStyles) },
                React.createElement("div", { className: cx({
                        [hiddenUntilTablet]: inHeader,
                    }) },
                    React.createElement("div", { className: messageStyles }, "Thank you"),
                    React.createElement("div", { className: subMessageStyles },
                        "We're funded by",
                        React.createElement("span", { className: headerYellowHighlight }, ` ${numberOfSupporters} `),
                        "readers across Australia.",
                        React.createElement("br", null),
                        React.createElement(ThemeProvider, { theme: buttonBrand },
                            React.createElement(LinkButton, { priority: "secondary", showIcon: true, size: "small", href: "https://support.theguardian.com/aus-2020-map?INTCMP=Aus_moment_2020_frontend_header" }, "Hear from our supporters"))))));
        }
        return null;
    }
    return (React.createElement("div", { className: cx(inHeader && paddingStyles) },
        React.createElement("div", { className: cx({
                [hiddenUntilTablet]: inHeader,
            }) },
            React.createElement("div", { className: messageStyles }, "Support The\u00A0Guardian"),
            React.createElement("div", { className: subMessageStyles }, showAusMomentHeader ? (React.createElement("div", null,
                "We're funded by",
                React.createElement("span", { className: headerYellowHighlight }, ` ${numberOfSupporters} `),
                "readers across Australia.")) : (React.createElement("div", null, " Available for everyone, funded by readers"))),
            React.createElement("a", { className: linkStyles, href: urls.contribute, "data-link-name": `${dataLinkNamePrefix}contribute-cta` },
                "Contribute ",
                React.createElement(ArrowRightIcon, null)),
            React.createElement("a", { className: linkStyles, href: urls.subscribe, "data-link-name": `${dataLinkNamePrefix}subscribe-cta` },
                "Subscribe ",
                React.createElement(ArrowRightIcon, null))),
        React.createElement("div", { className: cx({
                [hiddenFromTablet]: inHeader,
                [hidden]: !inHeader,
            }) }, edition === 'UK' ? (React.createElement("a", { className: linkStyles, href: urls.contribute, "data-link-name": `${dataLinkNamePrefix}contribute-cta` },
            "Contribute ",
            React.createElement(ArrowRightIcon, null))) : (React.createElement("a", { className: linkStyles, href: urls.support, "data-link-name": `${dataLinkNamePrefix}support-cta` },
            "Support us ",
            React.createElement(ArrowRightIcon, null))))));
};
